import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { EchartGenericChartComponent } from '@/app/charts/echart-generic-chart/echart-generic-chart.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ThemeService } from '@/app/services/theme/theme.service';
import {
  BusinessStatsAggregateResponseDto,
  BusinessStatsLeaderboardMetric,
  StatsBusinessLeaderboardModel,
  StatsFunnelBarModel,
  StatsKpiCardModel,
  StatsMetricChartModel,
  StatsPeriodSelection,
} from '../../admin-stats-aggregate/admin-stats-aggregate.types';
import {
  buildDailyBarChart,
  buildFunnelBars,
  chartVariantForMetricKey,
  companyInitials,
  funnelBarClass,
  getKpiTrend,
  kpiAccentDotClass,
  mapMostActiveBusinessRows,
  topCreatorBarClass,
} from '../../admin-stats-dashboard/admin-stats-dashboard.helpers';
import { formatStatsPeriodLabel } from '../../admin-stats-period/admin-stats-period.helpers';
import { AdminBusinessStatsService } from '../../../services/admin-business-stats.service';
import { AdminStatsPeriodFilter } from '../../admin-stats-period-filter/admin-stats-period-filter';
import { withShoutAgencyBrandFilter } from '../business-stats.helpers';
import { BusinessStatsSortField } from '../business-stats.types';

type LeaderboardSpec = {
  key: BusinessStatsLeaderboardMetric;
  title: string;
  subtitle: string;
  secondaryMetric: BusinessStatsLeaderboardMetric;
  secondarySuffix: string;
};

@Component({
  selector: 'admin-tool-business-stats-dashboard',
  imports: [AdminStatsPeriodFilter, LoadingInlineComponent, EchartGenericChartComponent],
  templateUrl: './business-stats-dashboard.html',
  styleUrl: './business-stats-dashboard.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessStatsDashboard implements OnDestroy {
  /** Hidden for now — business metrics are not a linear funnel (visits can exceed searches). */
  readonly showConversionFunnel = false;
  readonly mostActiveLimit = 5;

  private readonly leaderboardSpecs: readonly LeaderboardSpec[] = [
    {
      key: 'searches',
      title: 'Most active',
      subtitle: 'By searches',
      secondaryMetric: 'profileVisits',
      secondarySuffix: 'visits',
    },
    {
      key: 'profileVisits',
      title: 'Most profile visits',
      subtitle: 'By profile visits',
      secondaryMetric: 'searches',
      secondarySuffix: 'searches',
    },
    {
      key: 'invitesSent',
      title: 'Most invites',
      subtitle: 'By invites sent',
      secondaryMetric: 'profileVisits',
      secondarySuffix: 'visits',
    },
    {
      key: 'hires',
      title: 'Most hires',
      subtitle: 'By hires',
      secondaryMetric: 'invitesSent',
      secondarySuffix: 'invites',
    },
  ];

  aggregate: BusinessStatsAggregateResponseDto | null = null;
  kpiCards: StatsKpiCardModel[] = [];
  metricCharts: StatsMetricChartModel[] = [];
  funnelBars: StatsFunnelBarModel[] = [];
  leaderboards: StatsBusinessLeaderboardModel[] = [];
  rangeLabel = '';
  showShoutAgencyBrands = signal(true);
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  error: string | null = null;

  readonly companyInitials = companyInitials;
  readonly getKpiTrend = getKpiTrend;
  readonly kpiAccentDotClass = kpiAccentDotClass;
  readonly funnelBarClass = funnelBarClass;
  readonly topActiveBarClass = topCreatorBarClass;

  private subscriptionList = new Subscription();
  private periodStartDate = '';
  private periodEndDate = '';
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly adminBusinessStatsService = inject(AdminBusinessStatsService);
  private readonly themeService = inject(ThemeService);

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onShowShoutAgencyBrandsChange(show: boolean): void {
    this.showShoutAgencyBrands.set(show);
    if (this.periodStartDate && this.periodEndDate) {
      this.loadDashboard(this.periodStartDate, this.periodEndDate);
    }
  }

  onPeriodChange(selection: StatsPeriodSelection): void {
    this.loadDashboard(selection.startDate, selection.endDate);
  }

  private loadDashboard(startDate: string, endDate: string): void {
    this.periodStartDate = startDate;
    this.periodEndDate = endDate;
    this.subscriptionList.unsubscribe();
    this.subscriptionList = new Subscription();
    this.loadingState = 'loading';
    this.error = null;
    this.refreshView();

    const periodFilter = withShoutAgencyBrandFilter(
      { startDate, endDate },
      this.showShoutAgencyBrands(),
    );

    this.subscriptionList.add(
      forkJoin({
        aggregate: this.adminBusinessStatsService.aggregate(periodFilter),
        searches: this.adminBusinessStatsService.list(
          this.buildLeaderboardFilter(startDate, endDate, 'searches'),
        ),
        profileVisits: this.adminBusinessStatsService.list(
          this.buildLeaderboardFilter(startDate, endDate, 'profileVisits'),
        ),
        invitesSent: this.adminBusinessStatsService.list(
          this.buildLeaderboardFilter(startDate, endDate, 'invitesSent'),
        ),
        hires: this.adminBusinessStatsService.list(
          this.buildLeaderboardFilter(startDate, endDate, 'hires'),
        ),
      }).subscribe({
        next: ({ aggregate, searches, profileVisits, invitesSent, hires }) => {
          const leaderboardResults: Record<
            BusinessStatsLeaderboardMetric,
            { data: Parameters<typeof mapMostActiveBusinessRows>[0] }
          > = {
            searches,
            profileVisits,
            invitesSent,
            hires,
          };

          this.aggregate = aggregate;
          this.rangeLabel = formatStatsPeriodLabel(aggregate.current.startDate, aggregate.current.endDate);
          this.kpiCards = this.buildKpiCards(aggregate);
          this.metricCharts = this.buildMetricCharts(aggregate);
          if (this.showConversionFunnel) {
            this.funnelBars = buildFunnelBars(aggregate.current.searches, [
              { label: 'Profile visits', value: aggregate.current.profileVisits },
              { label: 'Invites sent', value: aggregate.current.invitesSent },
              { label: 'Hires', value: aggregate.current.hires },
            ]);
          } else {
            this.funnelBars = [];
          }
          this.leaderboards = this.leaderboardSpecs.map((spec) => ({
            key: spec.key,
            title: spec.title,
            subtitle: spec.subtitle,
            secondarySuffix: spec.secondarySuffix,
            rows: mapMostActiveBusinessRows(
              (leaderboardResults[spec.key]?.data ?? []).slice(0, this.mostActiveLimit),
              spec.key,
              spec.secondaryMetric,
            ),
          }));
          this.loadingState = 'loaded';
          this.refreshView();
        },
        error: () => {
          this.aggregate = null;
          this.kpiCards = [];
          this.metricCharts = [];
          this.funnelBars = [];
          this.leaderboards = [];
          this.loadingState = 'error';
          this.error = 'Unable to load business stats dashboard.';
          this.refreshView();
        },
      }),
    );
  }

  private buildLeaderboardFilter(
    startDate: string,
    endDate: string,
    sortField: BusinessStatsSortField,
  ) {
    return withShoutAgencyBrandFilter(
      {
        take: this.mostActiveLimit,
        skip: 0,
        sortField,
        sortOrder: 'DESC' as const,
        startDate,
        endDate,
      },
      this.showShoutAgencyBrands(),
    );
  }

  private buildKpiCards(data: BusinessStatsAggregateResponseDto): StatsKpiCardModel[] {
    return [
      {
        key: 'searches',
        label: 'Searches',
        current: data.current.searches,
        previous: data.previous.searches,
        accent: 'violet',
      },
      {
        key: 'profileVisits',
        label: 'Profile visits',
        current: data.current.profileVisits,
        previous: data.previous.profileVisits,
        accent: 'violet',
      },
      {
        key: 'invitesSent',
        label: 'Invites sent',
        current: data.current.invitesSent,
        previous: data.previous.invitesSent,
        accent: 'pink',
      },
      {
        key: 'hires',
        label: 'Hires',
        current: data.current.hires,
        previous: data.previous.hires,
        accent: 'pink',
      },
    ];
  }

  private buildMetricCharts(data: BusinessStatsAggregateResponseDto): StatsMetricChartModel[] {
    const specs = [
      {
        key: 'searches',
        label: 'Searches',
        pick: (point: (typeof data.daily)[number]) => point.searches,
      },
      {
        key: 'profileVisits',
        label: 'Profile visits',
        pick: (point: (typeof data.daily)[number]) => point.profileVisits,
      },
      {
        key: 'invitesSent',
        label: 'Invites sent',
        pick: (point: (typeof data.daily)[number]) => point.invitesSent,
      },
      {
        key: 'hires',
        label: 'Hires',
        pick: (point: (typeof data.daily)[number]) => point.hires,
      },
    ] as const;

    return specs.map((spec) => {
      const built = buildDailyBarChart(
        data.daily.map((point) => ({
          date: point.date,
          value: spec.pick(point),
        })),
      );

      return {
        key: spec.key,
        label: spec.label,
        chartOption: built.chartOption,
        chartXaxisData: built.chartXaxisData,
        hasData: built.hasData,
        chartVariant: chartVariantForMetricKey(spec.key, this.themeService.isDarkMode()),
      };
    });
  }

  private refreshView(): void {
    this.cdr.detectChanges();
  }
}
